import { Booking, Product, bookingStatusLabels } from "../../types/booking";
import { Invoice, PaymentTerm } from "../../types/invoice";
import { AIR_FREIGHT } from "../../lib/pricingDefaults";
import {
  COMMODITY_INSPECTION_FEE_EUR,
  getEffectiveCommodityInspection,
} from "../../utils/commodityInspectionUtils";
import { getPostExportLicenseFee } from "../../utils/postExportLicenseUtils";

// ─── Shared interfaces ────────────────────────────────────────────

export interface InvoiceLine {
  id?: number;
  description: string;
  quantity: number;
  unit_price: number;
  vat_percentage: number;
  cost_type?: string;
  template_source?: string;
  /** Pricing component calculation type (in-memory; not persisted to DB) */
  calculation_type?: string;
  /** True for lines added manually via wizard steps (tariff, customs, LMD) */
  manual?: boolean;
}

export interface FormValues {
  booking_id?: number | null;
  currency: string;
  payment_term: PaymentTerm;
  custom_payment_terms: string;
  lines: InvoiceLine[];
}

export interface PricingComponent {
  id: number;
  name: string;
  component_type: string;
  description?: string;
  calculation_type?: string;
}

export interface ContainerSize {
  id: number;
  name: string;
  created_at?: string;
}

export interface SuggestedTariff {
  id: number;
  price: number;
  description: string;
  from: string | null;
  to: string | null;
  isSmartMatch?: boolean;
  matchReasons?: string[];
  bracketDisplay?: string | null;
  bookingType?: string | null;
  containerSize?: string | null;
  containerSizeId?: number | null;
}

export interface TariffRow {
  unit_price: number;
  vat_percentage: number;
  container_type_id?: number;
  manual_quantity?: number;
  use_manual_quantity: boolean;
}

export interface InvoiceFormProps {
  onSuccess: (invoice: Invoice) => void;
  onCancel: () => void;
  initialBookingId?: number;
  editInvoice?: Invoice;
}

export type TariffCalculationType = "per_kg" | "per_cbm" | "per_container";

/** Import delivery (LMD) is never VAT-rated. */
export const LMD_COST_TYPES = new Set([
  "lmd",
  "lmd_delivery",
  "lmd_surcharge",
  "lmd_diesel_surcharge",
  "lmd_nl_pallet_surcharge",
]);

export const isLmdCostLine = (line: InvoiceLine | undefined): boolean => {
  if (!line) return false;
  if (line.cost_type && LMD_COST_TYPES.has(line.cost_type)) return true;
  return /^lmd\b/i.test(line.description || "");
};

export const hasDeliveryInvoiceLines = (lines: InvoiceLine[]): boolean =>
  lines.some(isLmdCostLine);

export const normalizeLmdLineVat = (lines: InvoiceLine[]): InvoiceLine[] =>
  zeroImportInvoiceVat(lines);

/** Import/freight forwarding invoices are never VAT-rated. */
export const zeroImportInvoiceVat = (lines: InvoiceLine[]): InvoiceLine[] =>
  lines.map((line) =>
    line.vat_percentage === 0 ? line : { ...line, vat_percentage: 0 }
  );

export interface TariffRankingContext {
  bookingType: number | null;
  bookingVolume: number;
  bookingContainerSize: number | null;
  transportTypeId: number | null;
}

// ─── Pure helper functions ─────────────────────────────────────────

export const getSecondsAgo = (date: Date): number => {
  return Math.floor((new Date().getTime() - date.getTime()) / 1000);
};

export const getBookingDisplayName = (booking: Booking): string => {
  const bookingId = `EFF${booking.id}`;
  const transportType = booking.transport_type_info
    ? Array.isArray(booking.transport_type_info)
      ? booking.transport_type_info[0]?.abbreviation || ""
      : booking.transport_type_info.abbreviation || ""
    : "";

  const transportTypeId = booking.transport_type_info
    ? Array.isArray(booking.transport_type_info)
      ? booking.transport_type_info[0]?.id
      : booking.transport_type_info.id
    : null;

  let bookingTypeInfo = "";
  if (transportTypeId === 2 && booking.booking_type_info) {
    const bookingType = Array.isArray(booking.booking_type_info)
      ? booking.booking_type_info[0]?.name || ""
      : booking.booking_type_info.name || "";
    if (bookingType) bookingTypeInfo = `-${bookingType}`;
  }

  const company = booking.company_info
    ? Array.isArray(booking.company_info)
      ? booking.company_info[0]?.name || "Unknown"
      : booking.company_info.name || "Unknown"
    : "Unknown";

  const status = booking.status || "";
  const statusLabel = status
    ? `[${bookingStatusLabels[status as keyof typeof bookingStatusLabels] || status}]`
    : "";

  let containerInfo = "";
  if (booking.container_info) {
    const container = Array.isArray(booking.container_info)
      ? booking.container_info[0]
      : booking.container_info;
    if (container?.reference) containerInfo = ` | Container: ${container.reference}`;
  }

  let deliveryInfo = "";
  const deliveryRows = booking.booking_delivery_info
    ? Array.isArray(booking.booking_delivery_info)
      ? booking.booking_delivery_info
      : [booking.booking_delivery_info]
    : [];
  deliveryInfo =
    deliveryRows.length > 0 ? " | Delivery" : " | In Storage";

  return `${bookingId}${transportType}${bookingTypeInfo} - ${company} ${statusLabel}${containerInfo}${deliveryInfo}`;
};

export const calculateTotal = (lines: InvoiceLine[]): number => {
  if (!lines || !Array.isArray(lines)) return 0;
  return lines.reduce((sum, line) => {
    if (!line) return sum;
    return sum + (line.quantity || 0) * (line.unit_price || 0);
  }, 0);
};

/** True when an invoice line is priced per carton (outbound, admin carton fee, etc.). */
export const isPerCartonInvoiceLine = (
  line: InvoiceLine,
  pricingComponents: PricingComponent[]
): boolean => {
  if (line.calculation_type === "per_carton") return true;

  if (/administration fee outgoing carton/i.test(line.description || "")) {
    return true;
  }

  const desc = (line.description || "").trim().toLowerCase();
  if (!desc) return false;

  const matched = pricingComponents.find((c) => {
    if (c.calculation_type !== "per_carton") return false;
    if (c.id.toString() === String(line.cost_type)) return true;
    if (c.name?.trim().toLowerCase() === desc) return true;
    if (c.description?.trim().toLowerCase() === desc) return true;
    return false;
  });
  if (matched) return true;

  return /per\s*carton|outbound cost per carton/i.test(line.description || "");
};

/** Outbound / administration lines that only apply to pallet shipments. */
export const isPalletOnlyOutboundLine = (
  line: InvoiceLine,
  pricingComponents: PricingComponent[]
): boolean => isPerPalletInvoiceLine(line, pricingComponents);

/** Administration lines that only apply to carton shipments. */
export const isCartonOnlyAdminLine = (line: InvoiceLine): boolean =>
  /administration fee outgoing carton/i.test(line.description || "");

/**
 * Keep calculation details in sync with qty:
 * - "(N pallets|cartons × €X)"
 * - "(N × €X)" (admin fees)
 * - leaked i18n keys from invoice generation without a locale context
 */
export const syncPerUnitCalculationDescription = (
  description: string,
  qty: number,
  unit: "pallets" | "cartons"
): string => {
  if (!description) return description;
  const i18nKey =
    unit === "pallets" ? "tools.calculator.pallets" : "tools.calculator.cartons";
  let next = description.replace(new RegExp(i18nKey.replace(/\./g, "\\."), "gi"), unit);

  const unitRe = new RegExp(`(\\()(\\d+)(\\s+${unit}\\s*×)`, "i");
  if (unitRe.test(next)) {
    return next.replace(unitRe, `$1${qty}$3`);
  }

  // Admin fees etc.: "Administration fee … (1 × €8.75)" → "(13 × €8.75)"
  const simpleRe = /\((\d+)\s*×\s*(€[\d.,]+)\)/;
  if (simpleRe.test(next)) {
    return next.replace(simpleRe, `(${qty} × $2)`);
  }

  return next;
};

/**
 * Align quantity of per-carton fee lines with the actual carton count
 * (from Sendcloud labels or booking products).
 */
export const syncPerCartonLineQuantities = (
  lines: InvoiceLine[],
  cartonCount: number,
  pricingComponents: PricingComponent[]
): { lines: InvoiceLine[]; changed: boolean } => {
  const qty = Math.floor(Number(cartonCount));
  if (!Number.isFinite(qty) || qty < 1 || !lines?.length) {
    return { lines, changed: false };
  }

  let changed = false;
  const updated = lines.map((line) => {
    if (!isPerCartonInvoiceLine(line, pricingComponents)) return line;
    const description = syncPerUnitCalculationDescription(
      line.description || "",
      qty,
      "cartons"
    );
    if (line.quantity === qty && description === line.description) return line;
    changed = true;
    return {
      ...line,
      quantity: qty,
      description,
      calculation_type: "per_carton" as const,
    };
  });

  return { lines: changed ? updated : lines, changed };
};

/**
 * Remove pallet-only outbound/admin lines in carton mode (and vice versa),
 * then sync per-unit quantities.
 */
export const syncOutboundFeesForShipmentType = (
  lines: InvoiceLine[],
  params: {
    shipmentType: "carton" | "pallet";
    cartonCount: number;
    palletCount: number;
    pricingComponents: PricingComponent[];
  }
): { lines: InvoiceLine[]; changed: boolean } => {
  const { shipmentType, cartonCount, palletCount, pricingComponents } = params;
  if (!lines?.length) return { lines, changed: false };

  let filtered = lines;
  let changed = false;

  if (shipmentType === "carton") {
    const next = lines.filter((l) => !isPalletOnlyOutboundLine(l, pricingComponents));
    if (next.length !== lines.length) {
      filtered = next;
      changed = true;
    }
    const synced = syncPerCartonLineQuantities(filtered, cartonCount, pricingComponents);
    return { lines: synced.lines, changed: changed || synced.changed };
  }

  const next = lines.filter((l) => !isCartonOnlyAdminLine(l));
  if (next.length !== lines.length) {
    filtered = next;
    changed = true;
  }
  const synced = syncPerPalletLineQuantities(filtered, palletCount, pricingComponents);
  return { lines: synced.lines, changed: changed || synced.changed };
};

/** True when an invoice line is priced per pallet (outbound, admin pallet fee, etc.). */
export const isPerPalletInvoiceLine = (
  line: InvoiceLine,
  pricingComponents: PricingComponent[]
): boolean => {
  if (line.calculation_type === "per_pallet") return true;

  // Flat-named but billed per pallet once LMD pallet count is known
  if (/administration fee outgoing pallet/i.test(line.description || "")) {
    return true;
  }

  const desc = (line.description || "").trim().toLowerCase();
  if (!desc) return false;

  const matched = pricingComponents.find((c) => {
    if (c.calculation_type !== "per_pallet") return false;
    if (c.id.toString() === String(line.cost_type)) return true;
    if (c.name?.trim().toLowerCase() === desc) return true;
    if (c.description?.trim().toLowerCase() === desc) return true;
    return false;
  });
  if (matched) return true;

  // Fallback when components aren't loaded yet (wizard session)
  return /per\s*pallet/i.test(line.description || "");
};

/**
 * Align quantity of per-pallet fee lines with the transport/LMD pallet count.
 * Returns the (possibly updated) lines and whether anything changed.
 */
export const syncPerPalletLineQuantities = (
  lines: InvoiceLine[],
  palletCount: number,
  pricingComponents: PricingComponent[]
): { lines: InvoiceLine[]; changed: boolean } => {
  const qty = Math.floor(Number(palletCount));
  if (!Number.isFinite(qty) || qty < 1 || !lines?.length) {
    return { lines, changed: false };
  }

  let changed = false;
  const updated = lines.map((line) => {
    if (!isPerPalletInvoiceLine(line, pricingComponents)) return line;
    const description = syncPerUnitCalculationDescription(
      line.description || "",
      qty,
      "pallets"
    );
    if (line.quantity === qty && description === line.description) return line;
    changed = true;
    return {
      ...line,
      quantity: qty,
      description,
      calculation_type: "per_pallet" as const,
    };
  });

  return { lines: changed ? updated : lines, changed };
};

export const getOriginPort = (booking: Booking) => {
  if (booking.container_info) {
    const containerInfo = Array.isArray(booking.container_info)
      ? booking.container_info[0]
      : booking.container_info;
    if (containerInfo?.origin_port) {
      const originPort = Array.isArray(containerInfo.origin_port)
        ? containerInfo.origin_port[0]
        : containerInfo.origin_port;
      if (originPort && originPort.name) {
        return { name: originPort.name, isFromContainer: true, containerRef: containerInfo.reference };
      }
    }
  }
  const originPort =
    booking.origin_port_info &&
    (Array.isArray(booking.origin_port_info) ? booking.origin_port_info[0] : booking.origin_port_info);
  return { name: originPort?.name || "Unknown", isFromContainer: false };
};

export const getDestinationPort = (booking: Booking) => {
  if (booking.container_info) {
    const containerInfo = Array.isArray(booking.container_info)
      ? booking.container_info[0]
      : booking.container_info;
    if (containerInfo?.destination_port) {
      const destPort = Array.isArray(containerInfo.destination_port)
        ? containerInfo.destination_port[0]
        : containerInfo.destination_port;
      if (destPort && destPort.name) {
        return { name: destPort.name, isFromContainer: true, containerRef: containerInfo.reference };
      }
    }
  }
  const destPort =
    booking.destination_port_info &&
    (Array.isArray(booking.destination_port_info) ? booking.destination_port_info[0] : booking.destination_port_info);
  return { name: destPort?.name || "Unknown", isFromContainer: false };
};

export const getBookingProducts = (booking: Booking): Product[] => {
  if (!booking.product_info) return [];
  if (Array.isArray(booking.product_info)) return booking.product_info as unknown as Product[];
  return [booking.product_info as unknown as Product];
};

export const getTotalWeightFromBooking = (booking: Booking): number => {
  // Round to avoid IEEE-754 noise (e.g. 330.2 + 219.3 → 549.5000000000001)
  return Number(
    getBookingProducts(booking)
      .reduce((sum, p) => sum + (p.gross_weight || 0), 0)
      .toFixed(3),
  );
};

export const getTotalVolumeFromBooking = (booking: Booking): number => {
  // Round to avoid IEEE-754 noise (e.g. 1.6 + 1.02 + 0.09 + 0.01 → 2.7199999999999998)
  return Number(
    getBookingProducts(booking)
      .reduce((sum, p) => sum + (p.cbm || 0), 0)
      .toFixed(3),
  );
};

export const calculateChargeableWeight = (booking: Booking) => {
  const totalWeight = getTotalWeightFromBooking(booking);
  const totalVolume = getTotalVolumeFromBooking(booking);
  const volumetricWeight = totalVolume * AIR_FREIGHT.VOLUMETRIC_FACTOR;
  return {
    actualWeight: totalWeight,
    volumetricWeight,
    chargeableWeight: Math.max(totalWeight, volumetricWeight),
  };
};

export const getIncoterm = (booking: Booking) => {
  if (!booking.booking_incoterm_info) return null;
  return Array.isArray(booking.booking_incoterm_info)
    ? booking.booking_incoterm_info[0]
    : booking.booking_incoterm_info;
};

export const getContainerReference = (booking: Booking): string | null => {
  if (!booking.container_info) return null;
  const containerInfo = Array.isArray(booking.container_info)
    ? booking.container_info[0]
    : booking.container_info;
  return containerInfo?.reference || null;
};

export const getTransportTypeId = (booking: Booking): number | null => {
  return booking.transport_type_info
    ? Array.isArray(booking.transport_type_info)
      ? booking.transport_type_info[0]?.id
      : booking.transport_type_info.id
    : null;
};

/** Resolve tariff unit from booking: air → per_kg, sea FCL → per_container, sea LCL → per_cbm. */
export const getTariffCalculationTypeForBooking = (
  booking: Booking
): TariffCalculationType => {
  const transportTypeId = getTransportTypeId(booking);
  if (transportTypeId === 1) return "per_kg";

  if (transportTypeId === 2) {
    if (booking.booking_type === 2) return "per_container";
    const bookingTypeName = booking.booking_type_info
      ? Array.isArray(booking.booking_type_info)
        ? booking.booking_type_info[0]?.name
        : booking.booking_type_info.name
      : null;
    return bookingTypeName === "FCL" ? "per_container" : "per_cbm";
  }

  return "per_cbm";
};

// ─── Payment term helpers ──────────────────────────────────────────

const PAYMENT_TERM_DAYS_MAP: Record<number, PaymentTerm> = {
  1: PaymentTerm.DAYS_1,
  7: PaymentTerm.DAYS_7,
  14: PaymentTerm.DAYS_14,
  30: PaymentTerm.DAYS_30,
  60: PaymentTerm.DAYS_60,
};

export const getPaymentTermFromBooking = (
  booking: Booking
): { paymentTermValue: PaymentTerm; customPaymentTerms: string } => {
  // When company has no payment terms configured, invoices default to Net 30.
  const defaultResult = { paymentTermValue: PaymentTerm.DAYS_30, customPaymentTerms: "" };
  const companyInfo = booking.company_info
    ? Array.isArray(booking.company_info) ? booking.company_info[0] : booking.company_info
    : null;
  if (!companyInfo?.pricing_payment_term) return defaultResult;

  const ptData = Array.isArray(companyInfo.pricing_payment_term)
    ? companyInfo.pricing_payment_term[0]
    : companyInfo.pricing_payment_term;
  if (!ptData || ptData.days === undefined) return defaultResult;

  const mapped = PAYMENT_TERM_DAYS_MAP[ptData.days];
  if (mapped) return { paymentTermValue: mapped, customPaymentTerms: "" };

  return {
    paymentTermValue: PaymentTerm.CUSTOM,
    customPaymentTerms: `Payment due within ${ptData.days} days`,
  };
};

// ─── Special fee helpers ───────────────────────────────────────────

export const addSpecialFeesToLines = (
  booking: Booking,
  existingLines: InvoiceLine[] = []
): InvoiceLine[] => {
  const lines = [...existingLines];

  if (booking.export_clearance === true) {
    if (!lines.some((l) => l.cost_type === "export_clearance")) {
      lines.push({ id: 0, description: "Export Clearance License Fee", quantity: 1, unit_price: 45, vat_percentage: 0, cost_type: "export_clearance" });
    }
  }

  if (booking.pick_up_fee && booking.pick_up_fee > 0) {
    if (!lines.some((l) => l.cost_type === "pickup_fee")) {
      lines.push({ id: 0, description: "Pick-up Fee", quantity: 1, unit_price: booking.pick_up_fee, vat_percentage: 0, cost_type: "pickup_fee" });
    }
  }

  if (getEffectiveCommodityInspection(booking)) {
    if (!lines.some((l) => l.cost_type === "commodity_inspection")) {
      lines.push({ id: 0, description: "Commodity Inspection", quantity: 1, unit_price: COMMODITY_INSPECTION_FEE_EUR, vat_percentage: 0, cost_type: "commodity_inspection" });
    }
  }

  const postExportLicenseFee = getPostExportLicenseFee(booking);
  if (postExportLicenseFee != null) {
    if (!lines.some((l) => l.cost_type === "post_export_license")) {
      lines.push({ id: 0, description: "Post Export License", quantity: 1, unit_price: postExportLicenseFee, vat_percentage: 0, cost_type: "post_export_license" });
    }
  }

  const companyInfo = booking.company_info
    ? Array.isArray(booking.company_info) ? booking.company_info[0] : booking.company_info
    : null;
  if (companyInfo?.pricing_payment_term) {
    const ptData = Array.isArray(companyInfo.pricing_payment_term)
      ? companyInfo.pricing_payment_term[0]
      : companyInfo.pricing_payment_term;
    if (ptData?.days === 30 && ptData?.interest_vat_import_duties === 4) {
      if (!lines.some((l) => l.cost_type === "payment_interest")) {
        const currentTotal = lines.reduce((sum, l) => {
          return l.cost_type !== "payment_interest" ? sum + l.unit_price * l.quantity : sum;
        }, 0);
        lines.push({
          id: 0, description: "Voorschotsprovisie (4%)", quantity: 1,
          unit_price: Math.round(currentTotal * 0.04 * 100) / 100,
          vat_percentage: 0, cost_type: "payment_interest",
        });
      }
    }
  }

  return lines;
};

export const recalculateInterestAmount = (lines: InvoiceLine[]): InvoiceLine[] => {
  const idx = lines.findIndex((l) => l.cost_type === "payment_interest");
  if (idx === -1) return lines;

  const otherTotal = lines.reduce((sum, l, i) => (i !== idx ? sum + l.unit_price * l.quantity : sum), 0);
  const newAmount = Math.round(otherTotal * 0.04 * 100) / 100;
  const updated = [...lines];
  updated[idx] = { ...updated[idx], unit_price: newAmount };
  return updated;
};

// ─── Tariff ranking types & FCL container size from product CBM ───

interface BracketInfo {
  id: number;
  from: number;
  to: number;
  transport_type: number | null;
}

/** Typical max loadable CBM by container size (conservative industry defaults). */
const FCL_SIZE_BY_CAPACITY: ReadonlyArray<{ label: string; capacity: number }> = [
  { label: "20ft", capacity: 33 },
  { label: "40ft", capacity: 67 },
  { label: "40ft HC", capacity: 76 },
];

/** Typical max loadable CBM by container size label (conservative industry defaults). */
export const getContainerCapacityCbm = (sizeName: string): number | null => {
  const n = sizeName.trim().toLowerCase();
  if (/45|40\s*ft\s*hc|40hc|hc\s*40|high\s*cube/.test(n)) return 76;
  if (/40\s*ft|40ft|40'|\b40\b/.test(n)) return 67;
  if (/20\s*ft|20ft|20'|\b20\b/.test(n)) return 33;
  return null;
};

/** Smallest standard FCL size that fits the volume (20ft → 40ft → 40ft HC). */
export const estimateFclContainerLabelFromVolume = (
  volume: number
): string | null => {
  if (volume <= 0) return null;
  const fit = FCL_SIZE_BY_CAPACITY.find((s) => volume <= s.capacity);
  return fit?.label ?? "40ft HC";
};

/** Smallest container size (from available tariffs) that fits the shipment volume. */
export const inferFclContainerSizeIdFromVolume = (
  volume: number,
  containerSizeInfo: Record<number, { id: number; name: string }>
): number | null => {
  if (volume <= 0 || Object.keys(containerSizeInfo).length === 0) return null;

  const sizes = Object.entries(containerSizeInfo)
    .map(([id, { name }]) => ({
      id: Number(id),
      name,
      capacity: getContainerCapacityCbm(name),
    }))
    .filter((s): s is typeof s & { capacity: number } => s.capacity != null)
    .sort((a, b) => a.capacity - b.capacity);

  if (sizes.length === 0) return null;

  const fitting = sizes.filter((s) => volume <= s.capacity);
  return (fitting.length > 0 ? fitting[0] : sizes[sizes.length - 1]).id;
};

export const volumeFitsContainerSize = (
  volume: number,
  sizeId: number,
  containerSizeInfo: Record<number, { id: number; name: string }>
): boolean => {
  const cap = getContainerCapacityCbm(containerSizeInfo[sizeId]?.name ?? "");
  return cap != null && volume > 0 && volume <= cap;
};

const isFclBookingType = (bookingType: number | null): boolean => bookingType === 2;

/** Prefer container record size; otherwise infer from total product CBM for FCL. */
export const resolveFclContainerSizeForRanking = (
  booking: Booking,
  containerSizeInfo: Record<number, { id: number; name: string }>,
  containerSizeFromRecord?: number | null
): number | null => {
  const bookingTypeId = booking.booking_type_info
    ? Array.isArray(booking.booking_type_info)
      ? booking.booking_type_info[0]?.id ?? null
      : booking.booking_type_info.id ?? null
    : booking.booking_type ?? null;

  if (!isFclBookingType(bookingTypeId)) return containerSizeFromRecord ?? null;

  if (containerSizeFromRecord != null) return containerSizeFromRecord;

  if (booking.container_info) {
    const c = Array.isArray(booking.container_info)
      ? booking.container_info[0]
      : booking.container_info;
    const fromJoin = (c as { container_size?: number | null })?.container_size;
    if (typeof fromJoin === "number") return fromJoin;
  }

  return inferFclContainerSizeIdFromVolume(
    getTotalVolumeFromBooking(booking),
    containerSizeInfo
  );
};

const tariffVolumeInBracket = (
  volume: number,
  bracket: BracketInfo
): boolean => {
  const high = Number(bracket.to);
  const useInclusiveUpper = high >= 999 || !Number.isFinite(high);
  return (
    volume >= Number(bracket.from) &&
    (useInclusiveUpper ? volume <= high : volume < high)
  );
};

// ─── Tariff ranking & formatting ───────────────────────────────────

export const rankAndFormatTariffs = (
  matchingTariffs: Array<{
    id: number; price: number; from: string | null; to: string | null;
    bookings_type: number | null; container_size: number | null;
    tariff_bracket: number | null;
  }>,
  bracketInfo: Record<number, BracketInfo>,
  bookingTypeInfo: Record<number, { id: number; name: string }>,
  containerSizeInfo: Record<number, { id: number; name: string }>,
  ctx: TariffRankingContext
): SuggestedTariff[] => {
  const isLCL = ctx.bookingType === 1;
  const isFCL = isFclBookingType(ctx.bookingType);
  // FCL bookings must not surface LCL tariffs (and vice versa). Untyped tariffs stay eligible.
  const tariffsForBookingType = ctx.bookingType
    ? matchingTariffs.filter(
        (t) => t.bookings_type == null || t.bookings_type === ctx.bookingType
      )
    : matchingTariffs;
  const inferredContainerSize =
    ctx.bookingContainerSize ??
    (isFCL && ctx.bookingVolume > 0
      ? inferFclContainerSizeIdFromVolume(ctx.bookingVolume, containerSizeInfo)
      : null);
  const containerSizeFromCbm =
    inferredContainerSize != null &&
    inferredContainerSize !== ctx.bookingContainerSize;

  const scored = tariffsForBookingType.map((t) => {
    let score = 0;
    const matchReasons: string[] = [];
    let volumeBracketMatch = false;

    if (ctx.bookingType && t.bookings_type === ctx.bookingType) {
      score += 100;
      matchReasons.push("booking type");
    }

    if (ctx.bookingVolume > 0 && t.tariff_bracket && bracketInfo[t.tariff_bracket]) {
      const b = bracketInfo[t.tariff_bracket];
      const volMatch = tariffVolumeInBracket(ctx.bookingVolume, b);
      const ttMatch = !b.transport_type || b.transport_type === ctx.transportTypeId;
      if (volMatch && ttMatch) {
        volumeBracketMatch = true;
        score += isLCL || isFCL ? 80 : 50;
        matchReasons.push("volume bracket");
        const range = b.to - b.from;
        if (range > 0) {
          const pos = (ctx.bookingVolume - b.from) / range;
          if ((isLCL || isFCL) && pos >= 0.3 && pos <= 0.7) {
            matchReasons.push("optimal volume");
          }
          score += (isLCL || isFCL ? 20 : 10) * (1 - Math.abs(0.5 - pos));
        }
      }
    }

    if (!isLCL && inferredContainerSize != null && t.container_size === inferredContainerSize) {
      score += isFCL ? 90 : 30;
      matchReasons.push(
        containerSizeFromCbm ? "CBM → container size" : "container size"
      );
    } else if (
      isFCL &&
      ctx.bookingVolume > 0 &&
      t.container_size != null &&
      volumeFitsContainerSize(ctx.bookingVolume, t.container_size, containerSizeInfo)
    ) {
      score += 40;
      if (!matchReasons.includes("volume fit")) matchReasons.push("volume fit");
    }

    if (isLCL) {
      if (t.tariff_bracket) score += 15;
    } else if (isFCL) {
      if (volumeBracketMatch) score += 15;
      else if (t.container_size === inferredContainerSize) score += 10;
    } else if (!t.tariff_bracket) {
      score += 10;
    }

    if (!t.bookings_type && !t.container_size && !t.tariff_bracket) {
      score += isLCL ? 5 : 10;
    }

    score += (1000 - (t.price || 0)) / 1000;

    return { tariff: t, score, matchReasons, isSmartMatch: matchReasons.length > 0 };
  });

  scored.sort((a, b) => b.score - a.score);

  return scored.map(({ tariff, isSmartMatch, matchReasons }, index) => {
    let description = `Option ${index + 1}`;
    if (isSmartMatch) {
      description += ` ✨ RECOMMENDED`;
      if ((isLCL || isFCL) && matchReasons.includes("volume bracket")) {
        description += ` (${ctx.bookingVolume.toFixed(1)} CBM fit)`;
      } else if (isFCL && matchReasons.includes("CBM → container size")) {
        const sizeName =
          tariff.container_size && containerSizeInfo[tariff.container_size]
            ? containerSizeInfo[tariff.container_size].name
            : inferredContainerSize && containerSizeInfo[inferredContainerSize]
              ? containerSizeInfo[inferredContainerSize].name
              : null;
        if (sizeName) {
          description += ` (${ctx.bookingVolume.toFixed(1)} CBM → ${sizeName})`;
        }
      }
    }
    if (tariff.bookings_type && bookingTypeInfo[tariff.bookings_type]) {
      description += ` - ${bookingTypeInfo[tariff.bookings_type].name}`;
    }
    if (tariff.container_size && containerSizeInfo[tariff.container_size]) {
      description += ` (${containerSizeInfo[tariff.container_size].name})`;
    }

    let bracketDisplay: string | null = null;
    if (tariff.tariff_bracket && bracketInfo[tariff.tariff_bracket]) {
      const b = bracketInfo[tariff.tariff_bracket];
      bracketDisplay = `${b.from}-${b.to} CBM`;
      description += ` [${bracketDisplay}]`;
    }

    return {
      id: tariff.id,
      price: tariff.price || 0,
      description,
      from: tariff.from,
      to: tariff.to,
      isSmartMatch,
      matchReasons,
      bracketDisplay,
      bookingType: tariff.bookings_type && bookingTypeInfo[tariff.bookings_type]
        ? bookingTypeInfo[tariff.bookings_type].name : null,
      containerSize: tariff.container_size && containerSizeInfo[tariff.container_size]
        ? containerSizeInfo[tariff.container_size].name : null,
      containerSizeId: tariff.container_size ?? null,
    };
  });
};
